<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu :pageTitle="pageTitle" />

    <div class="card border-0 shadow-none rounded-1 mb-25">
      <div class="card-body p-xl-40">
        <div class="row g-3 align-items-center">
          <div class="col-lg-8">
            <div class="d-flex flex-wrap align-items-center gap-2">
              <button
                type="button"
                class="btn style-two px-4 h-55"
                data-bs-toggle="modal"
                data-bs-target="#salesPaymentReportFilterModal"
              >
                {{ $t(`${i18nScope}.filter`) }}
                <i class="ri-filter-line ms-1"></i>
              </button>

              <div class="search-box position-relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  :placeholder="$t(`${i18nScope}.search_placeholder`)"
                  class="form-control bg-white h-55 shadow-none pe-5"
                />
                <span class="search-icon">
                  <i class="ri-search-line"></i>
                </span>
              </div>
            </div>
          </div>

          <div class="col-lg-4">
            <div class="d-flex justify-content-lg-end">
              <button
                type="button"
                class="btn style-three d-inline-flex align-items-center h-55 px-4"
                :disabled="exporting"
                @click="exportSalesPaymentReports"
              >
                <i class="ri-file-excel-2-line me-1"></i>
                {{ exporting ? $t(`${i18nScope}.exporting`) : $t(`${i18nScope}.excel`) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <SalesPaymentReport
      :payments="payments"
      :loading="loading"
      :pagination="pagination"
      :i18n-scope="i18nScope"
      @table-change="handleTableChange"
    />

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>

  <div class="modal fade" id="salesPaymentReportFilterModal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow">
        <div class="modal-header border-bottom-dashed">
          <h5 class="modal-title fw-bold">{{ $t(`${i18nScope}.advanced_filter`) }}</h5>
          <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" :aria-label="$t('common.close')"></button>
        </div>
        <div class="modal-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.warehouse`) }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.warehouse_id">
                <option :value="null">{{ $t(`${i18nScope}.all_warehouses`) }}</option>
                <option v-for="warehouse in warehouses" :key="warehouse.id" :value="warehouse.id">
                  {{ warehouse.name }}
                </option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.customer`) }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.customer_id">
                <option :value="null">{{ $t(`${i18nScope}.all_customers`) }}</option>
                <option v-for="customer in customers" :key="customer.id" :value="customer.id">
                  {{ customer.name }}
                </option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.payment_method`) }}</label>
              <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.method_id">
                <option :value="null">{{ $t(`${i18nScope}.all_payment_methods`) }}</option>
                <option v-for="method in paymentMethods" :key="method.id" :value="method.id">
                  {{ method.name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.from_date`) }}</label>
              <input v-model="filters.from_date" type="date" class="form-control bg-light border-0 fs-14 shadow-none" />
            </div>
            <div class="col-md-6">
              <label class="form-label fs-14 text-title">{{ $t(`${i18nScope}.to_date`) }}</label>
              <input v-model="filters.to_date" type="date" class="form-control bg-light border-0 fs-14 shadow-none" />
            </div>
          </div>
        </div>
        <div class="modal-footer border-top-dashed justify-content-between">
          <button type="button" class="btn btn-light" @click="resetFilters">{{ $t(`${i18nScope}.reset`) }}</button>
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="applyFilters">
            {{ $t(`${i18nScope}.apply_filter`) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import SalesPaymentReport from "../../components/Reports/SalesPaymentReport/SalesPaymentReport.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import { useSalesPaymentReport } from "../../composables/useSalesPaymentReport";

export default defineComponent({
  name: "SalesPaymentReportPage",
  components: {
    MainHeader,
    MainSidebar,
    BreadcrumbMenu,
    SalesPaymentReport,
    MainFooter,
  },
  setup() {
    return useSalesPaymentReport("sales");
  },
});
</script>

<style scoped>
.search-box {
  width: min(100%, 380px);
}

.search-box .form-control {
  padding-left: 18px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fff;
  color: #1f2940;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.search-box .form-control:focus {
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #8f9bb3;
  pointer-events: none;
  font-size: 18px;
}

.border-bottom-dashed,
.border-top-dashed {
  border-color: #e7ebf3 !important;
  border-style: dashed !important;
}
</style>
